/**
 * Shared predicates keep Insights navigation and route guards consistent and fail-closed while
 * retaining cached data. Paid-media dashboards are internal-only unless the backend filters their
 * queries to the caller's reporting scope, in which case that server-side scope is the boundary.
 */
import { isExternalUser } from 'src/hooks/isExternalUser';
import type {
  CampaignDesiredStateDto,
  PermissionAssignmentDto,
  UserContextDto,
} from 'src/store/generated/leela-api-generated';

export interface InsightsAccessInput {
  readonly assignments: readonly PermissionAssignmentDto[];
  readonly isError: boolean;
  readonly publishers: readonly string[];
}

export function hasInsightsPermissionData ({
  assignments,
  isError,
}: InsightsAccessInput): boolean {
  return !isError || assignments.length > 0;
}

export function canAccessInternalInsights (input: InsightsAccessInput): boolean {
  return hasInsightsPermissionData(input) && !isExternalUser(input.assignments);
}

export function canAccessPublisherInsights (
  input: InsightsAccessInput,
  publisher: CampaignDesiredStateDto['publisher'],
): boolean {
  return hasInsightsPermissionData(input) && input.publishers.includes(publisher);
}

export function canAccessPaidMediaDashboards (
  user: UserContextDto | undefined,
  isReportingQueryFilteringEnabled: boolean,
): boolean {
  if (user?.userType === 'internal') return true;
  return isReportingQueryFilteringEnabled && user?.userType === 'external';
}

export function canAccessDataAgent (user: UserContextDto | undefined): boolean {
  return user?.userType === 'internal';
}
